// @ts-nocheck
import { apiBaseUrl } from '../api/baseUrl';
import { onUserChange } from '../auth/session';
import { t } from '../i18n';
import { getAnalyticsConsent } from '../utils/analytics-consent-storage';
import {
  ensureGoogleFcQueue,
  waitForGoogleAdConsent,
} from '../utils/google-consent';
import { getCurrentView, onViewChange } from './view-router';
import { adsenseClientId, setPublicAdsenseClientId, shouldShowAds } from './pethernity';

type AdVariant = 'feed' | 'tempio' | 'memorial';

/** Display units from AdSense. Tempio reuses the former garden unit; memorial reuses profile. */
const ADSENSE_SLOTS: Record<AdVariant, string> = {
  feed: '2220005585',
  tempio: '1478654030',
  memorial: '8402270555',
};

function houseAdHtml(variant: string): string {
  return `
    <aside class="pethernity-ad pethernity-ad--${variant}" data-pethernity-ad>
      <p class="pethernity-ad__kicker">${t('pethernity.houseAdTitle')}</p>
      <p class="pethernity-ad__body">${t('pethernity.houseAdBody')}</p>
      <button type="button" class="pethernity-ad__cta" onclick="openPethernityPaywall()">
        ${t('pethernity.houseAdCta')}
      </button>
    </aside>
  `;
}

function canLoadAdsense(): boolean {
  return shouldShowAds() && !!adsenseClientId() && getAnalyticsConsent() === 'granted';
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

function adsenseUnitHtml(variant: AdVariant): string {
  const clientId = adsenseClientId();
  const slot = ADSENSE_SLOTS[variant];
  if (!clientId || !slot) return houseAdHtml(variant);
  const client = escapeAttr(clientId);
  const slotAttr = escapeAttr(slot);
  return `
    <ins class="adsbygoogle pethernity-adsense pethernity-adsense--${variant}"
         style="display:block"
         data-ad-client="${client}"
         data-ad-slot="${slotAttr}"
         data-pethernity-ad-variant="${variant}"
         data-ad-format="auto"
         data-full-width-responsive="false"></ins>
  `;
}

function swapInsToHouseAd(ins: HTMLElement): void {
  if (!ins.isConnected) return;
  if (ins.getAttribute('data-ad-status') === 'filled') return;
  if (!shouldShowAds()) return;
  const variant = (ins.getAttribute('data-pethernity-ad-variant') || '') as AdVariant;
  if (!ADSENSE_SLOTS[variant]) return;
  const host = ins.parentElement;
  if (host) host.innerHTML = houseAdHtml(variant);
}

function observeUnfilledFallback(ins: HTMLElement): void {
  if (ins.getAttribute('data-ad-status') === 'unfilled') {
    swapInsToHouseAd(ins);
    return;
  }

  const observer = new MutationObserver(() => {
    const status = ins.getAttribute('data-ad-status');
    if (status === 'filled') {
      observer.disconnect();
      return;
    }
    if (status === 'unfilled' || status === 'unfill-optimized') {
      observer.disconnect();
      swapInsToHouseAd(ins);
    }
  });
  observer.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] });
  window.setTimeout(() => {
    observer.disconnect();
    swapInsToHouseAd(ins);
  }, 8000);
}

export function fillAdsenseUnits(root: ParentNode = document): void {
  const units = [
    ...root.querySelectorAll(
      'ins.adsbygoogle:not([data-adsbygoogle-status]):not([data-adsense-queued])',
    ),
  ] as HTMLElement[];
  if (!units.length) return;

  units.forEach((ins) => ins.setAttribute('data-adsense-queued', '1'));

  const push = () => {
    const w = window as Window & { adsbygoogle?: Array<Record<string, unknown>> };
    w.adsbygoogle = w.adsbygoogle || [];
    units.forEach((ins) => {
      if (!ins.isConnected) return;
      observeUnfilledFallback(ins);
      try {
        w.adsbygoogle.push({});
      } catch (err) {
        console.warn('AdSense fill failed', err);
      }
    });
  };

  requestAnimationFrame(() => requestAnimationFrame(push));
}

let fundingChoicesLoad: Promise<'loaded' | 'missing'> | null = null;
let googleConsentWait: Promise<void> | null = null;

function loadFundingChoices(clientId: string): Promise<'loaded' | 'missing'> {
  ensureGoogleFcQueue();
  if (fundingChoicesLoad) return fundingChoicesLoad;

  fundingChoicesLoad = new Promise((resolve) => {
    let settled = false;
    const done = (state: 'loaded' | 'missing') => {
      if (settled) return;
      settled = true;
      resolve(state);
    };

    window.googlefc?.callbackQueue.push({
      CONSENT_DATA_READY: () => paintAdSlots(true),
    });

    const script = document.createElement('script');
    script.id = 'pethernity-funding-choices';
    script.async = true;
    script.src = `https://fundingchoicesmessages.google.com/i/${encodeURIComponent(clientId)}.js?ers=1`;
    script.onload = () => done('loaded');
    script.onerror = () => done('missing');
    document.head.appendChild(script);

    if (document.body && !window.frames['googlefcPresent']) {
      const iframe = document.createElement('iframe');
      iframe.name = 'googlefcPresent';
      iframe.setAttribute(
        'style',
        'width:0;height:0;border:0;position:absolute;left:-9999px;top:-9999px',
      );
      document.body.appendChild(iframe);
    }

    window.setTimeout(() => done('missing'), 4000);
  });

  return fundingChoicesLoad;
}

function ensureAdsenseScript(): void {
  if (!canLoadAdsense()) return;
  const clientId = adsenseClientId();
  if (!clientId) return;
  void loadFundingChoices(clientId);
  if (document.getElementById('pethernity-adsense')) return;
  const script = document.createElement('script');
  script.id = 'pethernity-adsense';
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(clientId)}`;
  document.head.appendChild(script);
}

async function whenGoogleAdConsentReady(): Promise<void> {
  if (!canLoadAdsense()) return;
  const clientId = adsenseClientId();
  if (!clientId) return;
  const fc = await loadFundingChoices(clientId);
  if (!googleConsentWait) {
    googleConsentWait = waitForGoogleAdConsent(fc === 'loaded' ? 25000 : 800);
  }
  await googleConsentWait;
}

export function renderAdSlot(variant: AdVariant | string): string {
  if (!shouldShowAds()) return '';
  if (canLoadAdsense()) return adsenseUnitHtml(variant as AdVariant);
  return houseAdHtml(variant);
}

function removeGardenSlots(): void {
  document.getElementById('garden-ad-desktop')?.remove();
  document.getElementById('garden-ad-mobile')?.remove();
}

function paintHost(id: string, variant: AdVariant, show: boolean, force = false): void {
  const host = document.getElementById(id);
  if (!host) return;
  if (!show) {
    host.innerHTML = '';
    host.hidden = true;
    return;
  }
  const wantsAdsense = canLoadAdsense();
  const hasAdsense = !!host.querySelector('ins.adsbygoogle');
  const filled = !!host.querySelector('ins.adsbygoogle[data-ad-status="filled"]');
  const hasHouse = !!host.querySelector('[data-pethernity-ad]');
  if (!force && ((wantsAdsense && hasAdsense) || (!wantsAdsense && hasHouse))) {
    host.hidden = false;
    return;
  }
  if (force && filled) {
    host.hidden = false;
    return;
  }
  host.innerHTML = renderAdSlot(variant);
  host.hidden = !shouldShowAds();
}

function paintDelegatedHosts(
  selector: string,
  variant: AdVariant,
  show: boolean,
  force = false,
): void {
  document.querySelectorAll(selector).forEach((el) => {
    const host = el as HTMLElement;
    if (!show) {
      host.innerHTML = '';
      host.hidden = true;
      return;
    }
    const hasAdsense = !!host.querySelector('ins.adsbygoogle');
    const filled = !!host.querySelector('ins.adsbygoogle[data-ad-status="filled"]');
    const hasHouse = !!host.querySelector('[data-pethernity-ad]');
    if (!force && ((canLoadAdsense() && hasAdsense) || (!canLoadAdsense() && hasHouse))) {
      host.hidden = false;
      return;
    }
    if (force && filled) {
      host.hidden = false;
      return;
    }
    host.innerHTML = renderAdSlot(variant);
    host.hidden = false;
  });
}

function hasTempioPublisherContent(): boolean {
  const slot = document.getElementById('tempio-ad-slot');
  return !!slot && slot.getAttribute('data-ads-ready') === '1';
}

function isMemorialModalEligible(): boolean {
  const modal = document.getElementById('view-modal');
  if (!modal || modal.style.display !== 'flex') return false;
  const slot = document.getElementById('memorial-ad-slot');
  return !!slot && slot.getAttribute('data-ads-ready') === '1';
}

export function refreshAds(): void {
  removeGardenSlots();
  if (canLoadAdsense()) {
    ensureAdsenseScript();
    void whenGoogleAdConsentReady().then(() => paintAdSlots());
    return;
  }
  paintAdSlots();
}

function paintAdSlots(force = false): void {
  const view = getCurrentView();
  const showAds = shouldShowAds();
  const showTempio = showAds && view === 'tempio' && hasTempioPublisherContent();
  const showMemorial = showAds && isMemorialModalEligible();
  const showFeed = showAds && view === 'community';

  paintHost('tempio-ad-slot', 'tempio', showTempio, force);
  paintHost('memorial-ad-slot', 'memorial', showMemorial, force);
  paintDelegatedHosts('[data-tempio-ad-host]', 'tempio', showTempio, force);
  paintDelegatedHosts('[data-feed-ad-host]', 'feed', showFeed, force);
  fillAdsenseUnits();
}

export function feedAdItemHtml(): string {
  if (!shouldShowAds()) return '';
  return `<div class="community-ad-slot" data-feed-ad-host>${renderAdSlot('feed')}</div>`;
}

export function tempioAdItemHtml(): string {
  if (!shouldShowAds()) return '';
  return `<div class="tempio-ad-slot tempio-ad-slot--grid" data-tempio-ad-host>${renderAdSlot('tempio')}</div>`;
}

export async function initAds(): Promise<void> {
  try {
    const res = await fetch(`${apiBaseUrl}/health`, { credentials: 'include' });
    const data = await res.json().catch(() => null);
    const id = data?.ads?.clientId;
    setPublicAdsenseClientId(typeof id === 'string' ? id : null);
  } catch {
    /* health is optional for house ads */
  }
  removeGardenSlots();
  refreshAds();
  onUserChange(() => refreshAds());
  onViewChange(() => refreshAds());
}
